/**
 * Phase 17.0 — Stripe Connect Express creator-facing card.
 *
 * Reusable component embedded on /payouts, /producer, /creator-hub,
 * /wallet. Renders the connection status, the right CTA, and a
 * one-liner of context per state:
 *
 *   not_connected         → "Connect Stripe Payout Account" (creates Express acct)
 *   onboarding_incomplete → "Finish Stripe Onboarding" (re-issues hosted URL)
 *   ready                 → "✓ Ready for payouts" + "Update payout info" (dashboard link)
 *   restricted            → "Account needs attention" + "Resolve in Stripe" (refresh link)
 *
 * Falls back to a "Stripe payouts coming soon" notice when the backend
 * reports `connect_configured: false` (no STRIPE_CONNECT_SECRET_KEY set
 * yet) so creators aren't shown a broken button before owner adds keys.
 *
 * Props:
 *   • variant — "live" | "beats" | "wallet" | "hub"  (affects copy only)
 *   • onStatusChange(status)  — optional callback when status loads/refreshes
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api from "@/lib/api";

const VARIANT_COPY = {
  live: {
    title: "Stripe Connect — Live & Gifts Payouts",
    subtitle: "Auto-receive gift earnings (≥ $25) directly to your bank.",
  },
  beats: {
    title: "Stripe Connect — Beat Sales Payouts",
    subtitle: "Auto-receive beat sales revenue (≥ $10) directly to your bank.",
  },
  wallet: {
    title: "Stripe Connect — Payout Account",
    subtitle: "Connect once to receive all RGM payouts automatically.",
  },
  hub: {
    title: "Stripe Connect — Creator Payouts",
    subtitle: "Connect your payout account to receive RGM earnings automatically.",
  },
};

const STATUS_META = {
  not_connected: {
    label: "Not connected",
    cls: "bg-gray-500/20 text-gray-200 border-gray-400/40",
    icon: "○",
  },
  onboarding_incomplete: {
    label: "Onboarding incomplete",
    cls: "bg-amber-500/20 text-amber-200 border-amber-400/40",
    icon: "◐",
  },
  ready: {
    label: "Ready for payouts",
    cls: "bg-emerald-500/20 text-emerald-200 border-emerald-400/40",
    icon: "●",
  },
  restricted: {
    label: "Needs attention",
    cls: "bg-rose-500/20 text-rose-200 border-rose-400/40",
    icon: "!",
  },
};

export default function StripeConnectCard({ variant = "wallet", onStatusChange }) {
  const [config, setConfig] = useState(null); // {connect_configured, publishable_key}
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const copy = VARIANT_COPY[variant] || VARIANT_COPY.wallet;
  const didLoad = useRef(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, s] = await Promise.all([
        api.get("/stripe/connect/config"),
        api.get("/stripe/connect/status"),
      ]);
      setConfig(c.data);
      setStatus(s.data);
      onStatusChange?.(s.data);
    } catch (e) {
      // 401 = not logged in (don't toast — pages already handle this)
      if (e?.response?.status !== 401) {
        toast.error("Couldn't load Stripe Connect status");
      }
    } finally {
      setLoading(false);
    }
  }, [onStatusChange]);

  useEffect(() => {
    if (didLoad.current) return;
    didLoad.current = true;
    load();
  }, [load]);

  // Phase 21.3a2 — Stripe onboard/link errors now come back as a
  // dict {message, stripe_error, key_mode[, account_id]}. Older code
  // treated `detail` as always-string and fell through to a generic
  // toast, silently hiding the real Stripe user_message. Unwrap the
  // dict shape here so users see the actual Stripe reason.
  const _stripeErrorMsg = (e, fallback) => {
    const d = e?.response?.data?.detail;
    if (typeof d === "string") return d;
    if (d && typeof d === "object" && d.message) return d.message;
    return fallback;
  };

  const startOnboarding = async () => {
    setBusy(true);
    try {
      const r = await api.post("/stripe/connect/onboard");
      const url = r?.data?.onboarding_url;
      if (!url) throw new Error("No onboarding URL returned");
      // Redirect to Stripe's hosted onboarding page in the SAME window so
      // the return_url brings them back to us cleanly.
      window.location.assign(url);
    } catch (e) {
      toast.error(_stripeErrorMsg(e, "Couldn't start Stripe onboarding"));
      setBusy(false);
    }
  };

  const refreshLink = async () => {
    setBusy(true);
    try {
      const r = await api.post("/stripe/connect/refresh-link");
      const url = r?.data?.onboarding_url;
      if (!url) throw new Error("No onboarding URL returned");
      window.location.assign(url);
    } catch (e) {
      toast.error(_stripeErrorMsg(e, "Couldn't refresh onboarding link"));
      setBusy(false);
    }
  };

  const openDashboard = async () => {
    setBusy(true);
    try {
      const r = await api.post("/stripe/connect/login-link");
      const url = r?.data?.url;
      if (!url) throw new Error("No dashboard URL returned");
      window.open(url, "_blank", "noopener");
    } catch (e) {
      toast.error(_stripeErrorMsg(e, "Couldn't open Stripe dashboard"));
    } finally {
      setBusy(false);
    }
  };

  // ── Render: Not configured on server ────────────────────────
  if (!loading && config && !config.connect_configured) {
    return (
      <div className="rounded-xl border border-white/10 bg-white/5 p-4 mb-4"
           data-testid={`stripe-connect-card-${variant}`}>
        <div className="flex items-center gap-3 mb-1">
          <div className="text-xl">⚡</div>
          <div>
            <div className="font-bold">{copy.title}</div>
            <div className="text-xs text-gray-400 mt-0.5">{copy.subtitle}</div>
          </div>
        </div>
        <div className="text-xs text-amber-300 mt-2"
             data-testid="stripe-connect-not-configured-notice">
          Stripe payouts are being set up by the RGM team. Manual payouts
          (PayPal, Zelle, CashApp, Bank, USDT) remain fully available below.
        </div>
      </div>
    );
  }

  if (loading || !status) {
    return (
      <div className="rounded-xl border border-white/10 bg-white/5 p-4 mb-4"
           data-testid={`stripe-connect-card-${variant}`}>
        <div className="text-xs text-gray-400">Loading Stripe Connect status…</div>
      </div>
    );
  }

  const meta = STATUS_META[status.stripe_connect_status] || STATUS_META.not_connected;
  const reqs = status.stripe_connect_requirements || {};
  const dueList = (reqs.currently_due || []).concat(reqs.past_due || []);

  return (
    <div className="rounded-xl border border-white/10 bg-gradient-to-br from-white/[0.07] to-white/[0.02] p-4 mb-4"
         data-testid={`stripe-connect-card-${variant}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
        <div className="flex items-center gap-3">
          <div className="text-xl">💳</div>
          <div>
            <div className="font-bold">{copy.title}</div>
            <div className="text-xs text-gray-400 mt-0.5">{copy.subtitle}</div>
          </div>
        </div>
        <span className={`text-[11px] font-bold px-2 py-1 rounded-full border ${meta.cls}`}
              data-testid={`stripe-connect-status-${status.stripe_connect_status}`}>
          {meta.icon} {meta.label}
        </span>
      </div>

      {/* State-specific body + CTA */}
      {status.stripe_connect_status === "not_connected" && (
        <>
          <p className="text-xs text-gray-300 mb-3">
            Connect a Stripe Express account once and we&apos;ll auto-deposit
            every approved payout to the bank you choose. Onboarding takes
            ~2 minutes and is hosted securely by Stripe.
          </p>
          <Button onClick={startOnboarding} disabled={busy}
                  className="bg-emerald-500 hover:bg-emerald-600 text-black font-bold"
                  data-testid="stripe-connect-start-onboarding-btn">
            {busy ? "Opening Stripe…" : "Connect Stripe Payout Account →"}
          </Button>
        </>
      )}

      {status.stripe_connect_status === "onboarding_incomplete" && (
        <>
          <p className="text-xs text-amber-200 mb-2">
            Your Stripe account is created but onboarding isn&apos;t complete.
            Finish the steps to start receiving automatic payouts.
          </p>
          {dueList.length > 0 && (
            <ul className="text-[11px] text-amber-100/80 mb-3 list-disc list-inside font-mono">
              {dueList.slice(0, 4).map((r) => <li key={r}>{r.replaceAll(".", " › ")}</li>)}
              {dueList.length > 4 && <li>+{dueList.length - 4} more</li>}
            </ul>
          )}
          <Button onClick={refreshLink} disabled={busy}
                  className="bg-amber-500 hover:bg-amber-600 text-black font-bold"
                  data-testid="stripe-connect-finish-onboarding-btn">
            {busy ? "Opening Stripe…" : "Finish Stripe Onboarding →"}
          </Button>
        </>
      )}

      {status.stripe_connect_status === "ready" && (
        <>
          <p className="text-xs text-emerald-200 mb-3">
            ✓ Your Stripe payout account is active. Approved payouts will
            land in your linked bank in 1–3 business days.
          </p>
          <div className="flex flex-wrap gap-2">
            <Button onClick={openDashboard} disabled={busy}
                    variant="outline"
                    className="border-emerald-400/40 text-emerald-100 hover:bg-emerald-500/10"
                    data-testid="stripe-connect-open-dashboard-btn">
              {busy ? "Opening…" : "Update payout info"}
            </Button>
            <Button onClick={load} disabled={busy}
                    variant="ghost" size="sm"
                    className="text-gray-300 hover:text-white"
                    data-testid="stripe-connect-refresh-status-btn">
              🔄 Refresh status
            </Button>
          </div>
        </>
      )}

      {status.stripe_connect_status === "restricted" && (
        <>
          <p className="text-xs text-rose-200 mb-2">
            ⚠ Your Stripe account needs attention before payouts can resume.
            {status.stripe_connect_disabled_reason && (
              <span className="block mt-1 font-mono text-[11px] opacity-80">
                Reason: {status.stripe_connect_disabled_reason}
              </span>
            )}
          </p>
          {dueList.length > 0 && (
            <ul className="text-[11px] text-rose-100/80 mb-3 list-disc list-inside font-mono">
              {dueList.slice(0, 4).map((r) => <li key={r}>{r.replaceAll(".", " › ")}</li>)}
            </ul>
          )}
          <div className="flex flex-wrap gap-2">
            <Button onClick={refreshLink} disabled={busy}
                    className="bg-rose-500 hover:bg-rose-600 text-white font-bold"
                    data-testid="stripe-connect-resolve-btn">
              {busy ? "Opening…" : "Resolve in Stripe →"}
            </Button>
            <Button onClick={openDashboard} disabled={busy}
                    variant="outline"
                    className="border-rose-400/40 text-rose-100 hover:bg-rose-500/10"
                    data-testid="stripe-connect-open-dashboard-btn-restricted">
              Open Stripe Dashboard
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
